import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';


import {DownloadButton} from 'src/components/form-buttons';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    SELECTION_KEY,
    UserSearchDownloadTool,
    UserSearchEditColumns,
    userSearchResultResultSelector,
} from 'src/modules/user-search-result';

import {Group} from '@pb/kit-component-group';

import {UsersAdvancedSearchBulkActions} from './bulk-actions/users-advanced-search-bulk-actions';

import type {ChildrenProps as UserSearchControllerProps} from 'src/modules/user-search-result/user-search-result.controller';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    getAllEntityIds: UserSearchControllerProps['getAllEntityIds'],
    place: string,
    adminId: number,
    isLoading: boolean,
    templates: UserSearchTemplate[],
    activeSearchTemplate: UserSearchTemplate,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    loadTemplates: () => void,
    loadActiveSearchTemplate: () => Promise<void>,
    refreshResults: () => void,
};

export const UsersSearchToolsRibbon = (props: Props) => {
    const {searchCriteriaDownload} = useEventTracking();

    const result = useSelector(userSearchResultResultSelector);
    const rows = useMemo(() => result?.data.map(({id}) => id) || [], [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    const {hasAccess} = useAdminAccessRight();

    return (
        <ToolsRibbonComponent {...{
            selectionKey: SELECTION_KEY,
            rows,
            totalCount,
            extraTools: ids => (
                <Group>
                    <UserSearchEditColumns
                        place={props.place}
                        adminId={props.adminId}
                        isLoading={props.isLoading}
                        templates={props.templates}
                        activeSearchTemplate={props.activeSearchTemplate}
                        setActiveSearchTemplate={props.setActiveSearchTemplate}
                        loadTemplates={props.loadTemplates}
                        loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                    />
                    {hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW) ? (
                        <UserSearchDownloadTool
                            userIds={ids}
                            getSortedEntityIds={props.getAllEntityIds}>
                            {handler => (
                                <DownloadButton
                                    disabled={!ids.length}
                                    handler={() => {
                                        searchCriteriaDownload({viewType: 'User'});
                                        handler();
                                    }}
                                />
                            )}
                        </UserSearchDownloadTool>
                    ) : null}
                </Group>
            ),
            maxSelectedEntities: MAX_SELECTED_ENTITIES,
            getAllEntityIds: props.getAllEntityIds,
        }}>
            {ids => (
                <UsersAdvancedSearchBulkActions
                    ids={ids as number[]}
                    onDone={props.refreshResults}
                />
            )}
        </ToolsRibbonComponent>
    );
};
